import { useEffect, useState } from 'react';
import '@/App.css';
import { getBackendUrl } from '../config/api';
import HomeHeader from '../components/home/HomeHeader';
import HeroSection from '../components/home/HeroSection';
import TrustStrip from '../components/home/TrustStrip';
import ProblemSection from '../components/home/ProblemSection';
import SolutionSection from '../components/home/SolutionSection';
import FeatureGrid from '../components/home/FeatureGrid';
import HowItWorks from '../components/home/HowItWorks';
import DomainAnalysisHighlight from '../components/home/DomainAnalysisHighlight';
import ExpertChatHighlight from '../components/home/ExpertChatHighlight';
import PublicAssistantHighlight from '../components/home/PublicAssistantHighlight';
import LearningBanner from '../components/home/LearningBanner';
import EcosystemSection from '../components/home/EcosystemSection';
import UseCaseGrid from '../components/home/UseCaseGrid';
import ComparisonTable from '../components/home/ComparisonTable';
import ResultsSection from '../components/home/ResultsSection';
import PricingSection from '../components/home/PricingSection';
import ContactSection from '../components/home/ContactSection';
import PartnerProgramSection from '../components/home/PartnerProgramSection';
import FinalCta from '../components/home/FinalCta';
import Footer from '../components/Footer';

import TuhiAvatar from '../components/TuhiAvatar';
const slideColors = [
  'bg-cv-sky/15',
  'bg-cv-sky/15',
  'bg-cv-teal/40',
  'bg-cv-gold/20',
  'bg-cv-orange/15',
  'bg-cv-sky/15',
  'bg-cv-sky/15',
  'bg-cv-teal/40',
  'bg-cv-sky/15',
  'bg-cv-sky/15',
];

const fallbackAISlide = {
  id: 'ai-fallback',
  category: 'SEO Pro Tip',
  icon: '✨',
  title: 'Optimize for AI Search',
  content: [
    'AI search engines now power 30%+ of queries',
    'Structured data helps AI understand your content',
    'Answer questions directly in your first paragraph',
    'Use semantic HTML for better comprehension',
  ],
  tip: 'Add FAQ schema to your top pages for 40% more AI visibility!',
  source: 'gemini',
  isAI: true,
};

// Brand-neutral AI chip for the Learning Center modal. We deliberately do
// NOT name the underlying LLM provider on user-facing surfaces — every
// AI-generated tip is presented as "TUHI AI" so the UX doesn't
// shift when the trainer routes to a different model. The actual
// provider (or `slm` / `slm-polished` for the SLM-first path) stays in
// `tip.source` for analytics only.
const AI_TIP_CHIP = {
  label: 'TUHI AI',
  accent: 'bg-cv-teal',
  icon: '✦',
  tipLabel: 'TUHI AI Tip',
};

const HomePage = () => {
  const [showLearningCenter, setShowLearningCenter] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [shuffledSlides, setShuffledSlides] = useState([]);
  const [allSlides, setAllSlides] = useState({ app: [], educational: [], gemini: [] });
  const [dynamicTotalAvailable, setDynamicTotalAvailable] = useState(0);
  const [slidesLoading, setSlidesLoading] = useState(false);

  useEffect(() => {
    document.title = 'Crawler Vision — Technical SEO & AI Crawler Readiness Platform';
  }, []);

  useEffect(() => {
    const loadSlides = async () => {
      const cachedData = localStorage.getItem('cv_learning_slides');
      const cachedExpiry = localStorage.getItem('cv_learning_slides_expiry');
      if (cachedData && cachedExpiry) {
        const expiry = new Date(cachedExpiry);
        if (expiry > new Date()) {
          try {
            const parsed = JSON.parse(cachedData);
            const cachedSlides = parsed?.slides || parsed;
            const cachedDynamicTotal = Number(
              parsed?.dynamicTotalAvailable ?? cachedSlides?.gemini?.length ?? 0
            );
            setAllSlides(cachedSlides);
            setDynamicTotalAvailable(Number.isFinite(cachedDynamicTotal) ? cachedDynamicTotal : 0);
            return;
          } catch {
            // ignore invalid cache
          }
        }
      }

      setSlidesLoading(true);
      const slides = { app: [], educational: [], gemini: [] };
      let cacheHours = 4;
      let dynamicTotalValue = 0;

      try {
        const staticResponse = await fetch('/data/learning-slides.json');
        if (staticResponse.ok) {
          const staticData = await staticResponse.json();
          if (staticData.slides) {
            staticData.slides.forEach((slide) => {
              if (slide.id.startsWith('edu-')) slides.educational.push(slide);
              else slides.app.push(slide);
            });
          }
        }
      } catch {
        // non-critical
      }

      try {
        const dynamicResponse = await fetch(`${getBackendUrl()}/api/learning/tips`);
        if (dynamicResponse.ok) {
          const dynamicData = await dynamicResponse.json();
          cacheHours = dynamicData.cacheHours || 24;
          const dynamicTotal = Number(
            dynamicData.total_available ?? dynamicData.count ?? dynamicData.tips?.length ?? 0
          );
          dynamicTotalValue = Number.isFinite(dynamicTotal) ? dynamicTotal : 0;
          setDynamicTotalAvailable(dynamicTotalValue);
          if (dynamicData.tips && dynamicData.tips.length > 0) {
            slides.gemini = dynamicData.tips.map((tip) => ({
              id: tip.id,
              category: tip.category,
              icon: tip.icon,
              title: tip.title,
              content: tip.content,
              tip: tip.tip,
              source: tip.source || 'gemini',
              type: tip.type || 'tip',
              isAI: true,
              createdAt: tip.createdAt,
              meta: tip.references || {},
            }));
          }
        }
      } catch {
        // non-critical
        dynamicTotalValue = 0;
        setDynamicTotalAvailable(0);
      }

      const expiry = new Date(Date.now() + cacheHours * 60 * 60 * 1000);
      localStorage.setItem('cv_learning_slides', JSON.stringify({
        slides,
        dynamicTotalAvailable: dynamicTotalValue,
      }));
      localStorage.setItem('cv_learning_slides_expiry', expiry.toISOString());
      setAllSlides(slides);
      setSlidesLoading(false);
    };

    loadSlides();
  }, []);

  useEffect(() => {
    if (showLearningCenter && (allSlides.app.length > 0 || allSlides.educational.length > 0)) {
      const selected = [];
      const shuffledColors = [...slideColors].sort(() => Math.random() - 0.5);
      const aiSlide = allSlides.gemini.length > 0
        ? { ...allSlides.gemini[0], bgColor: shuffledColors[0] }
        : { ...fallbackAISlide, bgColor: shuffledColors[0] };
      selected.push(aiSlide);

      const shuffledApp = [...allSlides.app].sort(() => Math.random() - 0.5);
      selected.push(
        ...shuffledApp.slice(0, 2).map((slide, idx) => ({ ...slide, bgColor: shuffledColors[selected.length + idx] }))
      );

      const eduCount = 10 - selected.length;
      const shuffledEdu = [...allSlides.educational].sort(() => Math.random() - 0.5);
      selected.push(
        ...shuffledEdu.slice(0, eduCount).map((slide, idx) => ({ ...slide, bgColor: shuffledColors[selected.length + idx] }))
      );

      setShuffledSlides(selected);
      setCurrentSlideIndex(0);
    }
  }, [showLearningCenter, allSlides]);

  const staticSlidesCount = allSlides.app.length + allSlides.educational.length;
  const fallbackDynamicCount = allSlides.gemini.length;
  const effectiveDynamicCount = dynamicTotalAvailable > 0 ? dynamicTotalAvailable : fallbackDynamicCount;
  const totalLearningCount = staticSlidesCount + effectiveDynamicCount;

  return (
    <div className="min-h-screen bg-cv-sky/10">
      <HomeHeader />

      <main data-cv-section="home-main">
        <div data-cv-section="home-hero"><HeroSection /></div>
        <div data-cv-section="home-trust"><TrustStrip /></div>
        <div data-cv-section="home-problem"><ProblemSection /></div>
        <div data-cv-section="home-solution"><SolutionSection /></div>
        <div data-cv-section="home-public-assistant"><PublicAssistantHighlight /></div>
        <div data-cv-section="home-expert-chat"><ExpertChatHighlight /></div>
        <div data-cv-section="home-domain-analysis"><DomainAnalysisHighlight /></div>
        <div data-cv-section="home-features" id="features"><FeatureGrid /></div>
        <div data-cv-section="home-how-it-works" id="how-it-works"><HowItWorks /></div>
        <div data-cv-section="home-learning-banner"><LearningBanner /></div>
        <div data-cv-section="home-ecosystem"><EcosystemSection /></div>
        <div data-cv-section="home-use-cases"><UseCaseGrid /></div>
        <div data-cv-section="home-comparison"><ComparisonTable /></div>
        <div data-cv-section="home-results"><ResultsSection /></div>
        <div data-cv-section="home-pricing" id="pricing"><PricingSection /></div>
        <div data-cv-section="home-partner-program"><PartnerProgramSection /></div>
        <div data-cv-section="home-contact" id="contact"><ContactSection /></div>
        <div data-cv-section="home-final-cta"><FinalCta /></div>
      </main>

      <div data-cv-section="home-footer"><Footer /></div>

      {/* Floating Learning Center button */}
      <button
        type="button"
        onClick={() => setShowLearningCenter(true)}
        aria-label="Open Learning Center"
        title="Quick Learning Center"
        className="fixed top-20 right-6 z-40 p-3 bg-cv-gold/70 text-white rounded-2xl shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-200 group"
      >
        <span className="text-2xl block">💡</span>
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-cv-orange rounded-full animate-pulse flex items-center justify-center">
          <span className="text-[8px] font-bold text-white">NEW</span>
        </span>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-cv-navy text-white text-xs font-semibold px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
          Learning Center
        </span>
      </button>

      {/* Learning Center Modal */}
      {showLearningCenter && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="bg-cv-gold/70 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">💡</span>
                  <div>
                    <h2 className="text-xl font-bold text-white">Learning Center</h2>
                    <p className="text-cv-gold text-sm">Quick tips to master SEO & AI</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowLearningCenter(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-all"
                >
                  ✕
                </button>
              </div>
            </div>

            {slidesLoading || shuffledSlides.length === 0 ? (
              <div className="p-12 flex flex-col items-center justify-center">
                <div className="w-10 h-10 border-4 border-cv-gold/50 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-cv-navy/60">Loading learning tips...</p>
              </div>
            ) : (
              <>
                <div className={`p-6 ${shuffledSlides[currentSlideIndex]?.bgColor || 'bg-white'} transition-colors duration-300`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-white/80 backdrop-blur text-cv-navy text-xs font-semibold rounded-full shadow-sm">
                        {shuffledSlides[currentSlideIndex].category}
                      </span>
                      {shuffledSlides[currentSlideIndex].isAI && (
                        <span
                          className={`px-2 py-1 ${AI_TIP_CHIP.accent} text-white text-xs font-semibold rounded-full flex items-center gap-1 shadow-sm`}
                          title="Generated by TUHI AI"
                        >
                          <TuhiAvatar size={16} />
                          {AI_TIP_CHIP.label}
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-cv-navy/60 bg-white/60 px-2 py-0.5 rounded-full">
                      {currentSlideIndex + 1} / {shuffledSlides.length}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-4xl">{shuffledSlides[currentSlideIndex].icon}</span>
                    <h3 className="text-2xl font-bold text-cv-navy/90">
                      {shuffledSlides[currentSlideIndex].title}
                    </h3>
                  </div>

                  <ul className="space-y-2.5 mb-5">
                    {shuffledSlides[currentSlideIndex].content.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 text-cv-navy/80">
                        <span className="text-cv-navy mt-0.5">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>

                  {(() => {
                    const currentSlide = shuffledSlides[currentSlideIndex];
                    const isAI = currentSlide.isAI;
                    return (
                      <div className={`rounded-xl p-4 bg-white/70 backdrop-blur shadow-sm border ${isAI ? 'border-cv-sky/30' : 'border-cv-gold/50'}`}>
                        <div className="flex items-start gap-2">
                          {isAI ? <TuhiAvatar size={22} /> : <span className="text-lg">💡</span>}
                          <div>
                            <span className={`text-xs font-bold uppercase tracking-wide ${isAI ? 'text-cv-navy' : 'text-cv-navy'}`}>
                              {isAI ? AI_TIP_CHIP.tipLabel : 'Pro Tip'}
                            </span>
                            <p className={`text-sm mt-1 ${isAI ? 'text-cv-navy' : 'text-cv-navy'}`}>
                              {currentSlide.tip}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="px-6 pb-6 bg-white">
                  <div className="flex justify-center gap-1.5 mb-4">
                    {shuffledSlides.map((slide, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentSlideIndex(idx)}
                        className={`h-2 rounded-full transition-all ${
                          idx === currentSlideIndex
                            ? `w-6 ${slide.isAI ? AI_TIP_CHIP.accent : 'bg-cv-navy'}`
                            : 'w-2 bg-cv-navy/25 hover:bg-cv-navy/40'
                        }`}
                      />
                    ))}
                  </div>

                  <div className="mb-4 text-center">
                    <a href="/learning" className="text-sm text-cv-navy hover:text-cv-navy font-medium transition-colors">
                      📚 View all {totalLearningCount} tips in Learning Center →
                    </a>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setCurrentSlideIndex((prev) => (prev === 0 ? shuffledSlides.length - 1 : prev - 1))}
                      className="flex-1 py-2.5 px-4 bg-cv-navy/10 hover:bg-cv-navy/15 text-cv-navy/80 font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                      ← Previous
                    </button>
                    <button
                      onClick={() => {
                        if (currentSlideIndex === shuffledSlides.length - 1) {
                          setShowLearningCenter(false);
                        } else {
                          setCurrentSlideIndex((prev) => prev + 1);
                        }
                      }}
                      className="flex-1 py-2.5 px-4 bg-cv-navy text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                      {currentSlideIndex === shuffledSlides.length - 1 ? 'Done ✓' : 'Next →'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
